import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, AlertTriangle } from 'lucide-react';
import { format } from 'date-fns';
import { createBooking, updateBooking, createGuest, getGuests, checkBookingConflicts } from '@/lib/api';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { PhotoGallery } from '@/components/PhotoGallery';

export const BookingDialog = ({ 
  open, 
  onClose, 
  selectedDate, 
  properties, 
  selectedProperty,
  editingBooking = null,
}) => {
  const [loading, setLoading] = useState(false);
  const [guests, setGuests] = useState([]);
  const [showNewGuest, setShowNewGuest] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  const [checkingConflicts, setCheckingConflicts] = useState(false);
  const [formData, setFormData] = useState({
    property_id: selectedProperty?.id || '',
    guest_id: '',
    guest_name: '',
    check_in: selectedDate ? format(selectedDate, 'yyyy-MM-dd') : '',
    check_out: '',
    notes: '',
    total_price: 0,
  });
  const [newGuest, setNewGuest] = useState({
    name: '',
    email: '',
    phone: '',
  });
  const [checkInDate, setCheckInDate] = useState(selectedDate || null);
  const [checkOutDate, setCheckOutDate] = useState(null);
  const [bookingPhotos, setBookingPhotos] = useState([]);

  useEffect(() => {
    if (!open) return;
    fetchGuests();
    if (editingBooking) {
      setFormData({
        property_id: editingBooking.property_id || '',
        guest_id: editingBooking.guest_id || '',
        guest_name: editingBooking.guest_name || '',
        check_in: editingBooking.check_in || '',
        check_out: editingBooking.check_out || '',
        notes: editingBooking.notes || '',
        total_price: editingBooking.total_price || 0,
      });
      if (editingBooking.check_in) setCheckInDate(new Date(editingBooking.check_in + 'T00:00:00'));
      if (editingBooking.check_out) setCheckOutDate(new Date(editingBooking.check_out + 'T00:00:00'));
      setBookingPhotos(editingBooking.photos || []);
      return;
    }
    if (selectedProperty) {
      setFormData(prev => ({ ...prev, property_id: selectedProperty.id }));
    }
    if (selectedDate) {
      setCheckInDate(selectedDate);
      setFormData(prev => ({ ...prev, check_in: format(selectedDate, 'yyyy-MM-dd') }));
    }
  }, [open, selectedProperty, selectedDate, editingBooking]);

  // Check for conflicts when property or dates change
  useEffect(() => {
    const checkConflicts = async () => {
      if (formData.property_id && formData.check_in && formData.check_out) {
        setCheckingConflicts(true);
        try {
          const response = await checkBookingConflicts({
            property_id: formData.property_id,
            guest_id: formData.guest_id || 'temp',
            check_in: formData.check_in,
            check_out: formData.check_out,
          });
          // Filter out self when editing
          const filtered = (response.data.conflicts || []).filter(
            c => !editingBooking || c.booking_id !== editingBooking.id
          );
          setConflicts(filtered);
        } catch (error) {
          console.error('Error checking conflicts:', error);
          setConflicts([]);
        } finally {
          setCheckingConflicts(false);
        }
      } else {
        setConflicts([]);
      }
    };
    
    const debounceTimer = setTimeout(checkConflicts, 300);
    return () => clearTimeout(debounceTimer);
  }, [formData.property_id, formData.check_in, formData.check_out, formData.guest_id, editingBooking]);

  const fetchGuests = async () => {
    try {
      const response = await getGuests();
      setGuests(response.data);
    } catch (error) {
      console.error('Error fetching guests:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Only require property and dates
    if (!formData.property_id || !formData.check_in || !formData.check_out) {
      toast.error('Please select property and dates');
      return;
    }

    setLoading(true);
    try {
      let guestId = formData.guest_id || '';
      let guestName = formData.guest_name;

      // Create new guest if provided (only name required now)
      if (showNewGuest && newGuest.name) {
        const guestResponse = await createGuest({
          name: newGuest.name,
          email: newGuest.email || '',
          phone: newGuest.phone || ''
        });
        guestId = guestResponse.data.id;
        guestName = newGuest.name;
      } else if (guestId) {
        const selectedGuest = guests.find(g => g.id === guestId);
        guestName = selectedGuest?.name || '';
      }

      // Use direct guest name input or default
      if (!guestId && !guestName) {
        guestName = formData.guest_name || 'Guest';
      }

      if (editingBooking) {
        await updateBooking(editingBooking.id, {
          check_in: formData.check_in,
          check_out: formData.check_out,
          notes: formData.notes,
          total_price: formData.total_price,
        });
        toast.success('Booking updated successfully');
      } else {
        await createBooking({
          ...formData,
          guest_id: guestId || 'walk-in',
          guest_name: guestName,
        });
        toast.success('Booking created successfully');
      }
      resetForm();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create booking');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      property_id: selectedProperty?.id || '',
      guest_id: '',
      guest_name: '',
      check_in: '',
      check_out: '',
      notes: '',
      total_price: 0,
    });
    setNewGuest({ name: '', email: '', phone: '' });
    setShowNewGuest(false);
    setCheckInDate(null);
    setCheckOutDate(null);
    setConflicts([]);
    setBookingPhotos([]);
  };

  return (
    <Dialog open={open} onOpenChange={() => { resetForm(); onClose(); }}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto" data-testid="booking-dialog">
        <DialogHeader>
          <DialogTitle className="font-manrope">{editingBooking ? 'Edit Booking' : 'New Booking'}</DialogTitle>
          <DialogDescription className="text-slate-500">Guest info, property, dates and pricing.</DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Property Selection */}
          <div className="space-y-2">
            <Label>Property *</Label>
            <Select
              value={formData.property_id}
              onValueChange={(value) => setFormData(prev => ({ ...prev, property_id: value }))}
            >
              <SelectTrigger data-testid="booking-property-select">
                <SelectValue placeholder="Select property" />
              </SelectTrigger>
              <SelectContent>
                {properties.map((prop) => (
                  <SelectItem key={prop.id} value={prop.id}>
                    {prop.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Guest Selection */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Guest *</Label>
              <Button 
                type="button"
                variant="link" 
                size="sm"
                onClick={() => setShowNewGuest(!showNewGuest)}
                className="text-blue-600"
                data-testid="toggle-new-guest-btn"
              >
                {showNewGuest ? 'Select Existing' : 'Add New Guest'}
              </Button>
            </div>

            {showNewGuest ? (
              <div className="space-y-3 p-3 bg-slate-50 rounded-lg">
                <Input
                  placeholder="Guest Name *"
                  value={newGuest.name}
                  onChange={(e) => setNewGuest(prev => ({ ...prev, name: e.target.value }))}
                  data-testid="new-guest-name-input"
                />
                <Input
                  placeholder="Email *"
                  type="email"
                  value={newGuest.email}
                  onChange={(e) => setNewGuest(prev => ({ ...prev, email: e.target.value }))}
                  data-testid="new-guest-email-input"
                />
                <Input
                  placeholder="Phone (optional)"
                  value={newGuest.phone}
                  onChange={(e) => setNewGuest(prev => ({ ...prev, phone: e.target.value }))}
                  data-testid="new-guest-phone-input"
                />
              </div>
            ) : (
              <Select
                value={formData.guest_id}
                onValueChange={(value) => setFormData(prev => ({ ...prev, guest_id: value }))}
              >
                <SelectTrigger data-testid="booking-guest-select">
                  <SelectValue placeholder="Select guest" />
                </SelectTrigger>
                <SelectContent>
                  {guests.map((guest) => (
                    <SelectItem key={guest.id} value={guest.id}>
                      {guest.name} ({guest.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Check-in Date */}
          <div className="space-y-2">
            <Label>Check-in Date *</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal bg-slate-50",
                    !checkInDate && "text-muted-foreground"
                  )}
                  data-testid="booking-checkin-btn"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {checkInDate ? format(checkInDate, 'PPP') : 'Select date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={checkInDate}
                  onSelect={(date) => {
                    setCheckInDate(date);
                    setFormData(prev => ({ 
                      ...prev, 
                      check_in: date ? format(date, 'yyyy-MM-dd') : '' 
                    }));
                  }}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Check-out Date */}
          <div className="space-y-2">
            <Label>Check-out Date *</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal bg-slate-50",
                    !checkOutDate && "text-muted-foreground"
                  )}
                  data-testid="booking-checkout-btn"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {checkOutDate ? format(checkOutDate, 'PPP') : 'Select date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={checkOutDate}
                  onSelect={(date) => {
                    setCheckOutDate(date);
                    setFormData(prev => ({ 
                      ...prev, 
                      check_out: date ? format(date, 'yyyy-MM-dd') : '' 
                    }));
                  }}
                  disabled={(date) => checkInDate && date <= checkInDate}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Conflict Warning */}
          {conflicts.length > 0 && (
            <div 
              className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-2"
              data-testid="booking-conflict-warning"
            >
              <div className="flex items-center gap-2 text-red-700 font-medium">
                <AlertTriangle className="h-4 w-4" />
                <span>Booking Conflict Detected!</span>
              </div>
              <p className="text-sm text-red-600">
                This property is already booked during the selected dates:
              </p>
              <ul className="text-sm text-red-600 space-y-1">
                {conflicts.map((conflict) => (
                  <li key={conflict.id || `${conflict.check_in}-${conflict.check_out}`} className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
                    <span className="font-medium">{conflict.guest_name}</span>
                    <span>({conflict.check_in} to {conflict.check_out})</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Total Price */}
          <div className="space-y-2">
            <Label htmlFor="total_price">Total Price ($)</Label>
            <Input
              id="total_price"
              type="number"
              min="0"
              value={formData.total_price}
              onChange={(e) => setFormData(prev => ({ 
                ...prev, 
                total_price: parseFloat(e.target.value) || 0 
              }))}
              className="bg-slate-50"
              data-testid="booking-price-input"
            />
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes (optional)</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Any special requests..."
              className="bg-slate-50"
              rows={2}
              data-testid="booking-notes-input"
            />
          </div>

          {/* Photo Album (edit mode only) */}
          {editingBooking && (
            <div className="space-y-2">
              <Label>Photo Album</Label>
              <p className="text-xs text-slate-500">
                Attach check-out condition or welcome-ready shots for this stay.
              </p>
              <PhotoGallery
                photos={bookingPhotos}
                onSave={async (updatedPhotos) => {
                  try {
                    await updateBooking(editingBooking.id, { photos: updatedPhotos });
                    setBookingPhotos(updatedPhotos);
                    toast.success('Photos updated');
                  } catch (_) {
                    toast.error('Failed to update photos');
                  }
                }}
                kind="booking_photo"
                title="Booking Photos"
                labels={[
                  { value: 'check_out', display: 'Check-out' },
                  { value: 'welcome_ready', display: 'Welcome-ready' },
                ]}
                testIdPrefix="booking-gallery"
              />
            </div>
          )}

          <DialogFooter>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => { resetForm(); onClose(); }}
              data-testid="booking-cancel-btn"
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={loading || conflicts.length > 0}
              className={cn(
                "bg-slate-900 hover:bg-slate-800",
                conflicts.length > 0 && "opacity-50 cursor-not-allowed"
              )}
              data-testid="booking-submit-btn"
            >
              {loading
                ? (editingBooking ? 'Saving...' : 'Creating...')
                : conflicts.length > 0
                  ? 'Conflict Detected'
                  : editingBooking ? 'Save Changes' : 'Create Booking'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default BookingDialog;
